import { CheckIcon, XIcon } from "lucide-react";

import { usageSessionLookupLines } from "../../usage/usageSessionLookupLines";
import type { UsageSourceStatus } from "../../usage/useUsage";
import { USAGE_TOTALS_LABELS } from "./UsageSummaryPanel";
import { PROVIDER_ORDER } from "./usageProviders";

/**
 * Environments that answered without reading a single transcript.
 *
 * Answering is not the same as reporting. A scan whose directory is missing,
 * or which found no files inside it, still comes back `ok`-shaped and is drawn
 * as a success everywhere else on the page, so an environment that contributed
 * nothing left the totals looking complete. A missing directory reports zero
 * scanned files too, so one test covers both.
 *
 * Only transcript directories count. CMMD reads a ledger and always reports
 * zero files, which is not a gap. And an environment is only named when *every*
 * harness it reported came back empty: a Claude-only user has an empty Codex
 * home permanently, and calling that out on every render would make the notice
 * meaningless.
 */
function sourcesWithoutTranscripts(
  sources: readonly UsageSourceStatus[],
): readonly UsageSourceStatus[] {
  return sources.filter((source) => {
    const reported = (source.summary?.sources ?? []).filter(
      (entry) => entry.fingerprint.kind === "transcripts",
    );
    return reported.length > 0 && reported.every((entry) => entry.scannedFiles === 0);
  });
}

/**
 * Says plainly when the totals are incomplete or deliberately narrowed: a
 * source that failed, one that answered with no transcripts at all, one whose
 * transcripts another source already reported, the runtimes whose own Recorded
 * turns were dropped because CMMD's ledger already holds them, and what the
 * ledger's session lookup settled or could not settle. Sources that are still
 * answering never reach this notice; the page shows the loading skeleton until
 * every one is terminal.
 */
export function UsageCoverageNotice({
  sources,
  duplicateSources,
  staleSources,
  claimedRuntimeIds,
}: {
  readonly sources: readonly UsageSourceStatus[];
  readonly duplicateSources: readonly string[];
  readonly staleSources: readonly string[];
  readonly claimedRuntimeIds: readonly string[];
}) {
  const failed = sources.filter((source) => source.error !== null);
  const stale = sources.filter((source) => staleSources.includes(source.sourceId));
  const empty = sourcesWithoutTranscripts(sources);
  const lookupLines = usageSessionLookupLines(sources);
  if (
    failed.length === 0 &&
    stale.length === 0 &&
    empty.length === 0 &&
    duplicateSources.length === 0 &&
    claimedRuntimeIds.length === 0 &&
    lookupLines.length === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 border border-border px-3 py-2 text-xs text-muted-foreground">
      {failed.map((source) => (
        <span key={source.sourceId}>{source.label} could not report usage.</span>
      ))}
      {empty.map((source) => (
        <span key={source.sourceId}>No transcripts found on {source.label}.</span>
      ))}
      {stale.map((source) => (
        <span key={source.sourceId}>
          {source.label} runs an older version and is excluded from totals.
        </span>
      ))}
      {duplicateSources.length > 0 ? (
        <span>
          Counted once across environments sharing a transcript directory:{" "}
          {duplicateSources.join(", ")}
        </span>
      ) : null}
      {lookupLines.map((line) => (
        <span key={`${line.sourceId}:${line.kind}`} data-testid={`usage-lookup-${line.kind}`}>
          {line.text}
        </span>
      ))}
      {claimedRuntimeIds.length > 0 ? (
        <span>
          Counted from CMMD&apos;s ledger rather than the environment&apos;s own record:{" "}
          {claimedRuntimeIds.length === 1 ? "1 runtime" : `${claimedRuntimeIds.length} runtimes`}.
        </span>
      ) : null}
    </div>
  );
}

/**
 * Per-source progress while the page waits for every source to answer. Only
 * rendered with two or more; a lone source has nothing to enumerate.
 */
export function UsageSourceStrip({ sources }: { readonly sources: readonly UsageSourceStatus[] }) {
  const scanning = sources.filter((source) => source.summary === null && source.error === null);

  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border border-border px-3 py-2 text-xs">
      {sources.map((source) => (
        <UsageSourceChip key={source.sourceId} source={source} />
      ))}
      <span className="ms-auto text-muted-foreground">
        {scanning.length === 1
          ? "1 source still reporting"
          : `${scanning.length} sources still reporting`}
      </span>
    </div>
  );
}

function UsageSourceChip({ source }: { readonly source: UsageSourceStatus }) {
  if (source.summary !== null) {
    return (
      <span className="flex items-center gap-1 text-foreground">
        <CheckIcon className="size-3 text-success-foreground" aria-hidden />
        {source.label}
      </span>
    );
  }
  if (source.error !== null) {
    return (
      <span className="flex items-center gap-1 text-destructive">
        <XIcon className="size-3" aria-hidden />
        {source.label}
      </span>
    );
  }
  return <span className="text-muted-foreground">{source.label}…</span>;
}

/**
 * Static stand-in with the loaded page's shape: headline, lane split, chart,
 * totals and breakdown. No shimmer; blocks fill in exactly once when the last
 * source answers.
 */
export function UsageSkeleton({ resolution }: { readonly resolution: "day" | "hour" }) {
  return (
    <>
      <section className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <div className="h-10 w-36 rounded-sm bg-muted" />
            <div className="h-4 w-32 rounded-sm bg-muted" />
          </div>
          {PROVIDER_ORDER.map((provider) => (
            <div key={provider} className="flex flex-col gap-1">
              <div className="flex min-h-5 items-center justify-between gap-4">
                <span className="flex items-center gap-2">
                  <span className="size-2 shrink-0 rounded-full bg-muted" />
                  <span className="size-4 shrink-0 rounded-full bg-muted" />
                  <div className="h-3.5 w-20 rounded-sm bg-muted" />
                </span>
                <div className="h-3.5 w-14 rounded-sm bg-muted" />
              </div>
              <div className="h-4 w-36 rounded-sm bg-muted" />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-foreground">
            {resolution === "hour" ? "Hourly" : "Daily"} cost
          </h2>
          {/* Mirrors the chart's h-56 body and w-14 axis gutter so nothing
              relayouts when the real chart swaps in. */}
          <div className="flex flex-col gap-1">
            <div className="ml-16 h-56 rounded-sm bg-muted/35" />
            <div className="ml-16 h-4 rounded-sm bg-muted/35" />
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-medium text-foreground">Totals</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-1 md:grid-cols-5">
          {USAGE_TOTALS_LABELS.map((label) => (
            <div key={label} className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{label}</span>
              <div className="h-6 w-16 rounded-sm bg-muted" />
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-foreground">Breakdown</h2>
          <div className="h-touch w-28 rounded-lg bg-input/40 md:h-7" />
        </div>
        <div className="h-44 rounded-sm bg-muted/35" />
      </section>
    </>
  );
}
